import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import {
    Badge,
    Button,
    DrawerBody,
    DrawerHeader,
    DrawerHeaderTitle,
    MessageBar,
    MessageBarBody,
    MessageBarTitle,
    OverlayDrawer,
    Spinner,
    Switch,
} from '@fluentui/react-components';
import {
    ArrowClockwise20Regular,
    Dismiss24Regular,
    Info20Regular,
} from '@fluentui/react-icons';
import moment from 'moment';

import * as ComponentFunctions from './ConsumptionSyncProcessIAAnalysis.Functions';
import SyncAnalysisFindingCard from './SyncAnalysisFinding.Card';
import { IAAgentAvatar } from '../../../../commons/component/IAAgentAvatar/IAAgentAvatar.Component';
import { SyncAnalysisFinding, ISyncAnalysisFindingsFilters } from '../../../../models/Entities/Protocols/SyncAnalysis';
import { SyncAnalysisFindingTypeEnum } from '../../../../models/Enums/SyncAnalysisFindingTypeEnum';
import { SyncAnalysisStatusEnum } from '../../../../models/Enums/SyncAnalysisStatusEnum';
import { PaginationControlComponent } from '../../../../commons/component/PaginationControl/PaginationControl.Component';
import { SYNC_ANALYSIS_FINDINGS_DEFAULT_PAGE_SIZE } from '../../../../redux/middleware/ProtocolConsumptionSyncAnalysisMiddleware';

import './ConsumptionSyncProcessIAAnalysis.Styles.css';

const ConsumptionSyncProcessIAAnalysisComponent = (props: ComponentFunctions.Props) => {
    const { analysis } = props;

    // El servidor pagina y filtra; filtrar acá sólo alcanzaría a la página
    // cargada y dejaría mal los totales y el recorrido de páginas.
    const [selectedType, setSelectedType] = useState<SyncAnalysisFindingTypeEnum | undefined>(undefined);
    const [onlyNew, setOnlyNew] = useState<boolean>(false);
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(SYNC_ANALYSIS_FINDINGS_DEFAULT_PAGE_SIZE);

    const filters: ISyncAnalysisFindingsFilters = useMemo(
        () => ({ findingType: selectedType, onlyNew: onlyNew || undefined }),
        [selectedType, onlyNew]
    );

    // Cambiar un filtro vuelve a la primera página: quedarse en la 7 de un
    // listado que ahora tiene 2 mostraría una página vacía.
    useEffect(() => {
        setPageNumber(1);
    }, [selectedType, onlyNew, pageSize]);

    useEffect(() => {
        if (props.isOpen && props.SyncProcessId) {
            props.GetProtocolConsumptionSyncAnalysis(props.SyncProcessId);
        }
    }, [props.isOpen, props.SyncProcessId]);

    // Los hallazgos se piden recién cuando hay un análisis terminado que los tenga.
    const analysisId = analysis?.id;
    const canListFindings = ComponentFunctions.hasFindingsToShow(analysis);

    useEffect(() => {
        if (props.isOpen && analysisId && canListFindings) {
            props.GetProtocolConsumptionSyncAnalysisFindings(analysisId, pageNumber, pageSize, filters);
        }
    }, [props.isOpen, analysisId, canListFindings, pageNumber, pageSize, filters]);

    const onRefresh = () => {
        props.GetProtocolConsumptionSyncAnalysis(props.SyncProcessId);
        if (analysisId && canListFindings) {
            props.GetProtocolConsumptionSyncAnalysisFindings(analysisId, pageNumber, pageSize, filters);
        }
    };

    const statusVariant = ComponentFunctions.getStatusVariant(analysis?.status);
    const typeCounts = useMemo(() => ComponentFunctions.buildFindingTypeCounts(analysis), [analysis]);
    const totalFindings = useMemo(() => ComponentFunctions.getTotalFindingCount(analysis), [analysis]);

    const executedAt = analysis?.executedAt
        ? moment(analysis.executedAt).format('DD/MM/YYYY HH:mm')
        : undefined;

    const isFirstLoad = props.isGettingAnalysis && analysis === undefined;
    const notAnalysed = !props.isGettingAnalysis && analysis === null;

    return (
        <OverlayDrawer
            position="end"
            size="medium"
            open={props.isOpen}
            onOpenChange={(_, data) => {
                if (!data.open) props.onDismiss();
            }}
        >
            <DrawerHeader className="csa-header">
                <DrawerHeaderTitle
                    action={
                        <div className="csa-header__actions">
                            <Button
                                appearance="subtle"
                                icon={<ArrowClockwise20Regular />}
                                aria-label="Actualizar"
                                title="Actualizar"
                                disabled={props.isGettingAnalysis}
                                onClick={onRefresh}
                            />
                            <Button
                                appearance="subtle"
                                icon={<Dismiss24Regular />}
                                aria-label="Cerrar"
                                onClick={props.onDismiss}
                            />
                        </div>
                    }
                >
                    <span className="csa-header__title">
                        <IAAgentAvatar size={78} className="csa-header__titleAvatar" />
                        <span className="csa-header__titleText">
                            Análisis de la sincronización
                            <Badge appearance="filled" color="warning" size="small">PREVIEW</Badge>
                        </span>
                    </span>
                </DrawerHeaderTitle>
                <span className="csa-header__subtitle">
                    Revisión automática de la corrida: qué quedó sin liquidar y por qué.
                </span>
            </DrawerHeader>

            <DrawerBody className="csa-body">

                {isFirstLoad && (
                    <div className="csa-loading">
                        <Spinner size="tiny" />
                        <span>Buscando el análisis de esta corrida...</span>
                    </div>
                )}

                {/* Que la corrida no tenga análisis es un estado normal, no un error. */}
                {notAnalysed && !props.failOnGettingAnalysis && (
                    <MessageBar intent="info">
                        <MessageBarBody>
                            <MessageBarTitle>Sin análisis</MessageBarTitle>
                            Esta corrida todavía no fue analizada.
                        </MessageBarBody>
                    </MessageBar>
                )}

                {props.failOnGettingAnalysis && (
                    <MessageBar intent="error">
                        <MessageBarBody>
                            <MessageBarTitle>No se pudo obtener el análisis</MessageBarTitle>
                            {props.error?.ErrorMessage}
                        </MessageBarBody>
                    </MessageBar>
                )}

                {analysis && (
                    <>
                        {/* ── Estado ── */}
                        <section className={'csa-status csa-status--' + statusVariant}>
                            <div className="csa-status__row">
                                <span className={'csa-status__pill csa-status__pill--' + statusVariant}>
                                    {ComponentFunctions.getStatusLabel(analysis.status)}
                                </span>
                                {executedAt && <span className="csa-status__date">{executedAt}</span>}
                                {analysis.requestedBy && (
                                    <span className="csa-status__by">por {analysis.requestedBy}</span>
                                )}
                            </div>

                            {/* No haberse ejecutado por falta de indicios es una decisión
                                deliberada del proceso: se explica para que no se lea
                                como una falla ni como un análisis vacío. */}
                            {analysis.status === SyncAnalysisStatusEnum.SkippedNoIndicators && (
                                <p className="csa-status__note">
                                    <Info20Regular />
                                    No se ejecutó porque la verificación previa no encontró indicios de
                                    liquidación faltante. La corrida quedó registrada igual.
                                </p>
                            )}

                            {analysis.status === SyncAnalysisStatusEnum.Failed && analysis.errorMessage && (
                                <p className="csa-status__error">{analysis.errorMessage}</p>
                            )}
                        </section>

                        {/* ── Resumen ── */}
                        {analysis.summary && (
                            <section className="csa-summary">
                                <span className="csa-summary__title">Resumen</span>
                                <p className="csa-summary__text">{analysis.summary}</p>
                            </section>
                        )}

                        {/* ── Métricas ── */}
                        <section className="csa-metrics">
                            <div className="csa-metric">
                                <span className="csa-metric__label">Evidencia analizada</span>
                                <span className="csa-metric__value">{analysis.evidenceItemCount}</span>
                            </div>
                            <div className="csa-metric">
                                <span className="csa-metric__label">Hallazgos</span>
                                <span className="csa-metric__value">{totalFindings}</span>
                            </div>
                            <div className="csa-metric">
                                <span className="csa-metric__label">Pendientes</span>
                                <span className="csa-metric__value">{analysis.pendingFindingCount}</span>
                            </div>
                            {/* Sólo si hay: con la resolución de hallazgos sin usar, un
                                cero permanente es ruido en la fila. */}
                            {analysis.discardedFindingCount > 0 && (
                                <div className="csa-metric">
                                    <span className="csa-metric__label">Descartados</span>
                                    <span className="csa-metric__value">{analysis.discardedFindingCount}</span>
                                </div>
                            )}
                        </section>

                        {/* ── Hallazgos ── */}
                        {canListFindings && (
                            <section className="csa-findings">
                                <header className="csa-findings__head">
                                    <span className="csa-findings__title">Hallazgos</span>
                                    <Switch
                                        checked={onlyNew}
                                        onChange={(_, data) => setOnlyNew(!!data.checked)}
                                        label="Sólo nuevos"
                                    />
                                </header>

                                {/* Los contadores por tipo son a la vez el desglose y el
                                    filtro: son las mismas cifras que se querrían tocar. */}
                                {typeCounts.length > 0 && (
                                    <div className="csa-typeFilters" role="group" aria-label="Filtrar por tipo">
                                        <button
                                            type="button"
                                            className={
                                                'csa-typeChip' + (selectedType === undefined ? ' is-active' : '')
                                            }
                                            onClick={() => setSelectedType(undefined)}
                                        >
                                            Todos
                                            <span className="csa-typeChip__count">{totalFindings}</span>
                                        </button>
                                        {typeCounts.map(({ descriptor, count }) => (
                                            <button
                                                key={descriptor.type}
                                                type="button"
                                                title={descriptor.description + ' Ancla en: ' + descriptor.anchor + '.'}
                                                className={
                                                    'csa-typeChip csa-typeChip--type' + descriptor.type +
                                                    (selectedType === descriptor.type ? ' is-active' : '')
                                                }
                                                onClick={() =>
                                                    setSelectedType(
                                                        selectedType === descriptor.type ? undefined : descriptor.type
                                                    )
                                                }
                                            >
                                                {descriptor.label}
                                                <span className="csa-typeChip__count">{count}</span>
                                            </button>
                                        ))}
                                    </div>
                                )}

                                {props.failOnGettingFindings && (
                                    <MessageBar intent="error">
                                        <MessageBarBody>
                                            <MessageBarTitle>No se pudieron obtener los hallazgos</MessageBarTitle>
                                            {props.error?.ErrorMessage}
                                        </MessageBarBody>
                                    </MessageBar>
                                )}

                                {props.isGettingFindings && props.findings.length === 0 && (
                                    <div className="csa-loading">
                                        <Spinner size="extra-tiny" />
                                        <span>Cargando hallazgos...</span>
                                    </div>
                                )}

                                {!props.isGettingFindings && props.findings.length === 0 && (
                                    <p className="csa-findings__empty">
                                        {selectedType !== undefined || onlyNew
                                            ? 'Ningún hallazgo coincide con el filtro.'
                                            : 'El análisis no dejó hallazgos.'}
                                    </p>
                                )}

                                {props.findings.length > 0 && (
                                    <ul className="csa-findings__list">
                                        {props.findings.map((finding: SyncAnalysisFinding) => (
                                            <SyncAnalysisFindingCard key={finding.id} finding={finding} />
                                        ))}
                                    </ul>
                                )}

                                <PaginationControlComponent
                                    paginationState={props.findingsPagination}
                                    showedRecords={props.findings.length}
                                    isLoading={props.isGettingFindings}
                                    setPageNumber={setPageNumber}
                                    setPageSize={setPageSize}
                                />
                            </section>
                        )}
                    </>
                )}
            </DrawerBody>
        </OverlayDrawer>
    );
};

export default connect(
    ComponentFunctions.mapStateToProps,
    ComponentFunctions.mapDispatchToProps
)(ConsumptionSyncProcessIAAnalysisComponent as any) as React.FC<ComponentFunctions.IProps>;
